import { markRaw, type Component } from 'vue';
import NotificationButton from './NotificationButton';
import { assertIsNonNullable } from '../../typeAssertions/undefined';
import { Variant } from '../../../vue/components/Elements/Button/Variant';
import type NotificationCheckbox from './NotificationCheckbox';
import AlertTriangleIcon from '../../../images/alert-triangle.svg?component';

export const enum NotificationVariant {
    PRIMARY = 'primary',
    TITLE_WITH_MESSAGE = 'title_with_message',
}

export type NotificationProps = {
    text?: string,
    show?: boolean,
    warning?: string,
    icon?: Component|null,
    withCancel?: boolean,
    title?: string,
    variant?: NotificationVariant,
    cancelText?: string,
    cancelVariant?: Variant,
    iconVariant?: Variant,
}

export type NotificationContent = {
    component: Component,
    props: Record<string, unknown>,
};

export default class Notification {
    public readonly text: string = 'Are you sure?';

    public readonly title: string = '';

    public readonly variant: NotificationVariant = NotificationVariant.PRIMARY;

    public readonly show: boolean = true;

    public readonly warning: string | null = null;

    public readonly icon: Component|null = markRaw(AlertTriangleIcon);

    public actionButtons: NotificationButton[] = [];

    public cancelButton: NotificationButton | null = null;

    public checkbox: NotificationCheckbox | null = null;

    public content: NotificationContent | null = null;

    public readonly iconVariant: Variant = Variant.DARK;

    constructor({
        text, show, warning,
        icon, withCancel = true,
        title, variant,
        cancelText,
        cancelVariant,
        iconVariant,
    }: NotificationProps = {}) {
        if (title) {
            this.title = title;
        }

        if (variant) {
            this.variant = variant;
        }
        if (text !== undefined) {
            this.text = text;
        }

        if (show !== undefined) {
            this.show = show;
        }

        if (warning !== undefined) {
            this.warning = warning;
        }

        if (icon !== undefined) {
            this.icon = icon;
        }

        if (iconVariant !== undefined) {
            this.iconVariant = iconVariant;
        }

        if (withCancel) {
            this.cancelButton = new NotificationButton(
                cancelText ?? 'Cancel',
                () => undefined,
                cancelVariant,
            );
        }
    }

    public addActionButton(notificationButton: NotificationButton): void {
        this.actionButtons.push(notificationButton);
    }

    public setCancelButtonText(text: string): void {
        assertIsNonNullable(this.cancelButton);
        this.cancelButton.text = text;
    }

    public addCheckbox(checkbox: NotificationCheckbox): void {
        this.checkbox = checkbox;
    }

    public setContent(component: Component, props: Record<string, unknown> = {}): void {
        this.content = { component: markRaw(component), props };
    }

    public getCheckboxState(): boolean {
        return this.checkbox ? this.checkbox.getCheckboxState() : false;
    }
}
